import { Session, StockSymbol } from '@benzinga/session';
import { FieldTracker, Quote, QuoteFeedEvent, QuotesV3Manager } from '@benzinga/quotes-v3-manager';
import { Subscription, Subscribable, SubscriberId, SubscribableMultiplexerEvent } from '@benzinga/subscribable';
import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';

import { Holding, WatchlistId, WatchlistManager } from '@benzinga/watchlist-manager';

import { HoldingsQuote } from './entities';
import { FeedMultiplexer } from './feedMultiplexer';

interface QuotesUpdateEvent {
  quote: HoldingsQuote;
  type: 'quote-holdings:update';
}

export type QuoteHoldingsFeedEvent = QuotesUpdateEvent;
export class QuoteHoldingsFeed extends Subscribable<QuoteHoldingsFeedEvent> {
  private symbols: StockSymbol[];
  private holding = new Map<StockSymbol, (Holding & { watchlistId?: WatchlistId })[]>();
  private holdingTotal = new Map<StockSymbol, Holding & { watchlistId?: WatchlistId }>();
  private watchlistManager: WatchlistManager;
  private watchlistSubscription: Subscription<WatchlistManager> | undefined;

  private quotesManager: QuotesV3Manager;
  private quotesManagerSubscription: Subscription<QuotesV3Manager> | undefined;
  private quotesMultiplexer: FeedMultiplexer;
  private quotesMultiplexerSubscription: Subscription<FeedMultiplexer> | undefined;

  private setIntervalHandle: ReturnType<typeof setInterval> | undefined;
  private quoteHoldings = new Map<StockSymbol, HoldingsQuote>([]);
  private quoteHoldingList?: HoldingsQuote[];

  private fieldsTracker: FieldTracker;

  constructor(symbols: StockSymbol[], subscribeToQuotes: boolean, session: Session) {
    super();
    this.symbols = symbols;

    this.watchlistManager = session.getManager(WatchlistManager);
    this.quotesManager = session.getManager(QuotesV3Manager);
    this.fieldsTracker = new FieldTracker(
      this.onFieldsUpdate,
      session.getManager(QuotesV3FieldsManager).getFieldIds() as Set<keyof Quote>,
    );

    this.getHoldings();
    this.quotesMultiplexer = new FeedMultiplexer(
      subscribeToQuotes ? symbols.map(symbol => [symbol, this.quotesManager.createQuoteFeed(symbol)]) : [],
      session,
    );
    symbols.forEach(s => this.getQuoteHolding(s));
  }

  public getCachedValue = (): HoldingsQuote[] => {
    if (this.quoteHoldingList === undefined) {
      this.quoteHoldingList = Array.from(this.quoteHoldings.values());
    }
    return this.quoteHoldingList;
  };

  public getSymbols = () => this.symbols;

  public addSymbols = (symbols: StockSymbol[]) => {
    this.symbols = [...this.symbols, ...symbols];
    symbols.forEach(s => {
      this.quotesMultiplexer.add(s, this.quotesManager.createQuoteFeed(s));
    });
    symbols.forEach(s => {
      this.dispatch({ quote: this.getQuoteHolding(s), type: 'quote-holdings:update' });
    });
    this.getHoldings();
  };
  public removeSymbols = (symbols: StockSymbol[]) => {
    this.symbols = this.symbols.filter(symbol => !symbols.includes(symbol));
    symbols.forEach(s => {
      this.quotesMultiplexer.remove(s);
    });
  };

  protected override onFirstSubscription = (args: Set<keyof Quote>) => {
    this.quotesMultiplexerSubscription = this.quotesMultiplexer.subscribe(this.onSubscriptionFeedCall, args);

    this.watchlistSubscription = this.watchlistManager.subscribe(event => {
      let affectedSymbols: Set<StockSymbol>;
      let updates: { quote: HoldingsQuote; type: 'quote-holdings:update' }[];

      switch (event.type) {
        case 'watchlist:updated_watchlists':
          affectedSymbols = new Set<StockSymbol>();

          // Get all symbols that need updating
          this.watchlistManager.getStoredWatchlists()?.forEach(watchlist => {
            watchlist.symbols.forEach(symbol => {
              if (symbol.holding !== undefined) {
                affectedSymbols.add(symbol.symbol);
              }
            });
          });

          // Clear only affected symbols
          affectedSymbols.forEach(symbol => {
            this.quoteHoldings.delete(symbol);
          });
          this.quoteHoldingList = undefined;

          // Batch update holdings
          this.getHoldings();

          // Only update subscription if fields have changed
          if (this.quotesMultiplexerSubscription) {
            const currentFields = this.fieldsTracker.getFields();
            if (!this.areSetsEqual(currentFields, args)) {
              this.quotesMultiplexerSubscription.unsubscribe();
              this.quotesMultiplexerSubscription = this.quotesMultiplexer.subscribe(this.onSubscriptionFeedCall, args);
            }
          }

          // Batch dispatch updates
          updates = Array.from(affectedSymbols).map(symbol => ({
            quote: this.getQuoteHolding(symbol),
            type: 'quote-holdings:update' as const,
          }));

          // Dispatch all updates at once
          updates.forEach(update => this.dispatch(update));
      }
    });
  };

  protected override onZeroSubscriptions = () => {
    if (this.setIntervalHandle) {
      clearInterval(this.setIntervalHandle);
    }
    this.quotesMultiplexerSubscription?.unsubscribe();
    this.quotesManagerSubscription?.unsubscribe();
    this.watchlistSubscription?.unsubscribe();
  };

  protected override onUpdate = (id: SubscriberId, args: Set<keyof Quote>) => {
    this.fieldsTracker.onUpdate(id, args);
  };

  protected override onSubscribe = (id: SubscriberId, args: Set<keyof Quote>) => {
    this.fieldsTracker.onSubscribe(id, args);
  };

  protected override onUnsubscribe = (id: SubscriberId) => {
    this.fieldsTracker.onUnsubscribe(id);
  };

  private onFieldsUpdate = (fields: Set<keyof Quote>) => {
    this.quotesMultiplexerSubscription?.update(this.onSubscriptionFeedCall, fields);
  };

  private onSubscriptionFeedCall = (event: SubscribableMultiplexerEvent<QuoteFeedEvent>) => {
    switch (event.type) {
      case 'quote': {
        this.dispatch({
          quote: this.getQuoteHolding(event.quote.symbol ?? ''),
          type: 'quote-holdings:update',
        });
      }
    }
  };

  private getHoldings = () => {
    this.holding.clear();

    this.watchlistManager
      .getStoredWatchlists()
      ?.flatMap(w =>
        w.symbols
          .filter(s => s.holding !== undefined)
          .map(s => ({ symbol: s.symbol, ...(s.holding as Holding), watchlistId: w.watchlistId })),
      )
      ?.forEach(h => {
        const holdingTotal = this.holdingTotal.get(h.symbol);
        if (holdingTotal) {
          this.holdingTotal.set(h.symbol, {
            ...holdingTotal,
            averageBuyingPrice: (holdingTotal.costBasis + h.costBasis) / (holdingTotal.quantity + h.quantity),
            costBasis: holdingTotal.costBasis + h.costBasis,
            price: (holdingTotal.costBasis + h.costBasis) / (holdingTotal.quantity + h.quantity),
            quantity: holdingTotal.quantity + h.quantity,
          });
        } else {
          this.holdingTotal.set(h.symbol, h);
        }

        const holding = this.holding.get(h.symbol);
        if (holding) {
          holding.push(h);
        } else {
          this.holding.set(h.symbol, [h]);
        }
      });
  };

  private calculateHoldings = (quote, holding, holdingsId = 'Total') => {
    const lastPrice = quote?.price ?? 0;

    const acquiredPrice = holding?.price ?? 0;
    const acquiredQuantity = holding?.quantity ?? 0;

    const originalValue = acquiredPrice * acquiredQuantity;
    const currentValue = lastPrice * acquiredQuantity;

    const dollarChange = currentValue - originalValue;
    const isIncrease = dollarChange > 0;
    let percentChange = 0;
    if (isIncrease) {
      const increase = currentValue - originalValue;
      percentChange = (increase / originalValue) * 100;
    } else {
      const decrease = originalValue - currentValue;
      percentChange = -(decrease / originalValue) * 100;
    }

    return {
      [`portfolioCost${holdingsId}`]: originalValue,
      [`portfolioGainDollar${holdingsId}`]: lastPrice ? dollarChange : null,
      [`portfolioGainPercentage${holdingsId}`]: lastPrice ? percentChange : null,
      [`portfolioMarketValue${holdingsId}`]: lastPrice ? currentValue : null,
      [`portfolioQuantity${holdingsId}`]: acquiredQuantity,
    };
  };

  private getQuoteHolding = (symbol: StockSymbol): HoldingsQuote => {
    const holding = this.holdingTotal.get(symbol);
    const quote = this.quotesManager.getCachedQuote(symbol);

    const holdingQuotes: HoldingsQuote = {
      ...quote,
      ...this.calculateHoldings(holding, quote),
      ...(this.holding
        .get(symbol)
        ?.reduce((acc, h) => ({ ...acc, ...this.calculateHoldings(quote, h, h.watchlistId) }), {}) ?? {}),
      symbol,
    };

    this.quoteHoldings.set(symbol, holdingQuotes);
    this.quoteHoldingList = undefined;

    return holdingQuotes;
  };

  private areSetsEqual<T>(set1: Set<T>, set2: Set<T>): boolean {
    if (set1.size !== set2.size) return false;
    for (const item of set1) {
      if (!set2.has(item)) return false;
    }
    return true;
  }
}
